import dayjs from "dayjs";
import { setCache } from "store/slices/cacheSlice";
import { where } from "auth/FirebaseAuth";
import FirebaseService from "services/FirebaseService";
import FirebaseDatatypeEnum from "enum/FirebaseDatatype.enum";
import TipoWhere from "enum/TipoWhere.enum";
import store from "store";
import Utils from "utils";

// Models import
import User from "./user";
import Categoria from "./categoria";
import Cliente from "./cliente";
import FormaPagamento from "./forma-pagamento";
import Fornecedor from "./fornecedor";
import Produto from "./produto";
import ProdutoFluxo from "./produto-fluxo";
import Pedido from "./pedido";
import ClienteAnotacao from "./cliente-anotacao";
import Receita from "./receita";
import Despesa from "./despesa";
import Configuracao from "./configuracao";
// import Agenda from "./agenda";

const userID = () => store.getState().user.id;
const getCache = () => store.getState().cache;

const typesFormatters = {
	[FirebaseDatatypeEnum.STRING]: (value) => String(value),
	[FirebaseDatatypeEnum.NUMBER]: (value) => Number(value),
	[FirebaseDatatypeEnum.BOOLEAN]: (value) => Boolean(value),
	[FirebaseDatatypeEnum.ARRAY]: (value) => (Array.isArray(value) ? value : []),
	[FirebaseDatatypeEnum.NULL]: () => null,
	[FirebaseDatatypeEnum.TIMESTAMP]: (value) => dayjs(value).format(),
	[FirebaseDatatypeEnum.CUSTOM]: (value) => value,
};

const whereFunctions = {
	[TipoWhere.EQUAL]: (_, key, value) => (row) => row[key] === value,
	[TipoWhere.BETWEEN]: (type, key, value) => {
		if ([FirebaseDatatypeEnum.NUMBER, FirebaseDatatypeEnum.TIMESTAMP].includes(type)) {
			const types = {
				[FirebaseDatatypeEnum.NUMBER]: (row) => row[key] > value[0] && row[key] < value[1],
				[FirebaseDatatypeEnum.TIMESTAMP]: (row) => {
					const val = dayjs(row[key]);
					return (val.isAfter(value[0]) && val.isBefore(value[1])) || val.isSame(value[0]) || val.isSame(value[1]);
				},
			};

			return types[type];
		} else {
			throw new Error("Tipo inpróprio para o comparador 'BETWEEN'");
		}
	},
	[TipoWhere.LIKE]: (type, key, value) => {
		if ([FirebaseDatatypeEnum.STRING].includes(type)) {
			return (row) => Utils.compareStrings(row[key], value);
		} else {
			throw new Error("Tipo inpróprio para o comparador 'LIKE'");
		}
	},
	[TipoWhere.ILIKE]: (type, key, value) => {
		if ([FirebaseDatatypeEnum.STRING, FirebaseDatatypeEnum.NUMBER, FirebaseDatatypeEnum.TIMESTAMP].includes(type)) {
			return (row) => Utils.searchString(row[key], value);
		} else {
			throw new Error("Tipo inpróprio para o comparador 'ILIKE'");
		}
	},
	[TipoWhere.GREATER]: (type, key, value) => {
		if ([FirebaseDatatypeEnum.STRING, FirebaseDatatypeEnum.NUMBER, FirebaseDatatypeEnum.TIMESTAMP].includes(type)) {
			const types = {
				[FirebaseDatatypeEnum.STRING]: (row) => row[key].length > value.length,
				[FirebaseDatatypeEnum.NUMBER]: (row) => row[key] > value,
				[FirebaseDatatypeEnum.TIMESTAMP]: (row) => dayjs(row[key]).isAfter(dayjs(value)),
			};
			return types[type];
		} else {
			throw new Error("Tipo inpróprio para o comparador 'GREATER'");
		}
	},
	[TipoWhere.GREATER_EQUAL]: (type, key, value) => {
		if ([FirebaseDatatypeEnum.STRING, FirebaseDatatypeEnum.NUMBER, FirebaseDatatypeEnum.TIMESTAMP].includes(type)) {
			const types = {
				[FirebaseDatatypeEnum.STRING]: (row) => row[key].length >= value.length,
				[FirebaseDatatypeEnum.NUMBER]: (row) => row[key] >= value,
				[FirebaseDatatypeEnum.TIMESTAMP]: (row) => {
					const comp_val = dayjs(row[key]);
					const val = dayjs(value);
					return comp_val.isAfter(val) || comp_val.isSame(val);
				},
			};
			return types[type];
		} else {
			throw new Error("Tipo inpróprio para o comparador 'GREATER_EQUAL'");
		}
	},
	[TipoWhere.LOWER]: (type, key, value) => {
		if ([FirebaseDatatypeEnum.STRING, FirebaseDatatypeEnum.NUMBER, FirebaseDatatypeEnum.TIMESTAMP].includes(type)) {
			const types = {
				[FirebaseDatatypeEnum.STRING]: (row) => row[key].length < value.length,
				[FirebaseDatatypeEnum.NUMBER]: (row) => row[key] < value,
				[FirebaseDatatypeEnum.TIMESTAMP]: (row) => dayjs(row[key]).isBefore(dayjs(value)),
			};
			return types[type];
		} else {
			throw new Error("Tipo inpróprio para o comparador 'LOWER'");
		}
	},
	[TipoWhere.LOWER_EQUAL]: (type, key, value) => {
		if ([FirebaseDatatypeEnum.STRING, FirebaseDatatypeEnum.NUMBER, FirebaseDatatypeEnum.TIMESTAMP].includes(type)) {
			const types = {
				[FirebaseDatatypeEnum.STRING]: (row) => row[key].length <= value.length,
				[FirebaseDatatypeEnum.NUMBER]: (row) => row[key] <= value,
				[FirebaseDatatypeEnum.TIMESTAMP]: (row) => {
					const comp_val = dayjs(row[key]);
					const val = dayjs(value);
					return comp_val.isBefore(val) || comp_val.isSame(val);
				},
			};
			return types[type];
		} else {
			throw new Error("Tipo inpróprio para o comparador 'LOWER_EQUAL'");
		}
	},
	[TipoWhere.ARRAY_CONTAIN]: (type, key, value) => {
		if ([FirebaseDatatypeEnum.ARRAY].includes(type)) {
			return (row) => row[key].includes(value);
		} else {
			throw new Error("Tipo inpróprio para o comparador 'ARRAY_CONTAIN'");
		}
	},
	[TipoWhere.IN]: (_, key, value) => (row) => value.includes(row[key]),
};

const dataFormatter = (model, defaults, data, ignoreUndefs = false) => {
	if (data) {
		if (Array.isArray(data)) {
			return data.map((row) =>
				Object.keys(model).reduce((acc, key) => {
					if (row[key] !== undefined) {
						acc[key] = row[key] !== null ? typesFormatters[model[key]](row[key]) : null;
					} else if (!ignoreUndefs) {
						acc[key] = defaults[key] ?? null;
					}

					return acc;
				}, {})
			);
		} else {
			return Object.keys(model).reduce((acc, key) => {
				if (data[key] !== undefined) {
					acc[key] = data[key] !== null ? typesFormatters[model[key]](data[key]) : null;
				} else if (!ignoreUndefs) {
					acc[key] = defaults[key] ?? null;
				}

				return acc;
			}, {});
		}
	}
};

const paramsConstructor = (model, params, connections) => {
	const newModel = { ...model };

	const newParams = {
		where: params.where
			? Object.keys(params.where)
					.filter((key) => "deletedAt" === key)
					.map((key) => where(key, params.where[key].op, params.where[key].value))
			: [],
		join: params.join
			?.map((ass) => connections.relations.find((rel) => rel.name === ass))
			.reduce((acc, row) => {
				newModel[row.name] = FirebaseDatatypeEnum.CUSTOM;
				if (!acc[row.model.ModelName]) {
					acc[row.model.ModelName] = row;
				}
				return acc;
			}, {}),
		children: params.children
			?.map((ass) => connections.children.find((rel) => rel.name === ass))
			.reduce((acc, row) => {
				newModel[row.name] = FirebaseDatatypeEnum.CUSTOM;
				if (!acc[row.model.ModelName]) {
					acc[row.model.ModelName] = row;
				}
				return acc;
			}, {}),
		where_after: params.where
			? Object.keys(params.where)
					.filter((key) => "deletedAt" !== key)
					.map((key) => whereFunctions[params.where[key].op](model[key], key, params.where[key].value))
			: [],
	};

	return { newParams, newModel };
};

class Model {
	#defaults = {};
	#models = {};
	#id;
	#keys;
	#configs;
	#relations;
	#children;
	#relate;
	#assume;
	#parent;

	constructor({ keys, configs, id }) {
		this.#keys = keys;
		this.#configs = configs;
		this.#id = id ?? ""
	}

	clone(id, condition = () => true){
		if(condition(this.#configs)){
			const newModel = new Model({
				keys: this.#keys,
				configs: this.#configs,
				id
			})
	
			const newModels = {...this.#models};
	
			newModels[newModel.ModelName] = newModel;
	
			Object.keys(newModels).reduce((acc, key) => {
				const createdModel = newModels[key].clone(undefined, (config) => config.parent === newModel.ModelName);

				if(createdModel){
					newModels[key] = createdModel;
					acc.push(newModels[key]);
				}

				return acc;
			}, []).forEach((row) => row.doConnections(newModels))

			newModel.assume = this.#assume;
			newModel.relate = this.#relate;
			newModel.defaults = this.#defaults;
	
			newModel.doConnections(this.#models, {...this.#models});
	
			return newModel;
		} else {
			return false;
		}
	}

	get ModelName() {
		return this.#configs.model;
	}

	/**
	 * @param {Function} callback
	 */
	set relate(callback) {
		this.#relate = callback;
	}

	/**
	 * @param {Function} callback
	 */
	set assume(callback) {
		this.#assume = callback;
	}

	/**
	 * @param {Object} obj
	 */
	set defaults(obj) {
		this.#defaults = obj;
	}

	get path() {
		let path = "";

		if (this.#parent) {
			path += this.#parent.path;
		}

		path += `${this.#configs.collection}/${this.#id === "" ? "" : `${this.#id}/`}`;

		return path;
	}

	id(id) {
		return this.clone(id);
	}

	doConnections(models) {
		this.#models = models;

		if (this.#relate) {
			this.#relations = this.#relate(models);
		}

		if (this.#assume) {
			this.#children = this.#assume(models);
		}

		if (this.#configs.parent) {
			this.#parent = models[this.#configs.parent];
			models[this.#configs.parent][this.#configs.model] = this;
		}
	}

	async findAll(params = {}, force = false) {
		params.where = params.where ?? {};

		if (this.#configs.softDelete) {
			params.where.deletedAt = {
				op: "==",
				value: null,
			};
		}

		const { newParams, newModel } = paramsConstructor(this.#keys, params, { relations: this.#relations, children: this.#children });

		let firstData = [];

		const cache = getCache();

		if (cache[this.path] && !force) {
			firstData = cache[this.path];
		} else {
			firstData = await FirebaseService.getCollection(this.path, newParams.where);

			store.dispatch(
				setCache({
					[this.path]: [...firstData],
				})
			);
		}

		firstData = firstData.filter((row) =>
			newParams.where_after.reduce((acc, func) => {
				if (acc) {
					acc = func(row);
				}

				return acc;
			}, true)
		);

		if (params.join?.length > 0) {
			const joinQuery = {};

			await Promise.all(
				Object.keys(newParams.join).map((key) => {
					const getJoinData = async () => {
						joinQuery[key] = await newParams.join[key].model.findAll({}, force);
					};

					return getJoinData();
				})
			);

			firstData = firstData.map((row) => {
				const newRow = { ...row };
				Object.values(newParams.join).forEach((ass) => {
					newRow[ass.name] = joinQuery[ass.model.ModelName].find((joinRow) => joinRow.id === row[ass.fk]);
				});

				return newRow;
			});
		}

		if (params.children?.length > 0) {
			const childQuery = {};

			await Promise.all(
				Object.keys(newParams.children).map((key) => {
					childQuery[key] = {};
					return Promise.all(
						firstData.map((row) => {
							const getJoinData = async () => {
								childQuery[key][row.id] = await this.id(row.id)[key].findAll({}, force);
							};

							return getJoinData();
						})
					);
				})
			);

			firstData = firstData.map((row) => {
				const newRow = { ...row };
				Object.keys(newParams.children).forEach((key) => {
					newRow[newParams.children[key].name] = childQuery[key][newRow.id];
				});

				return newRow;
			});
		}

		return dataFormatter(newModel, this.#defaults, firstData, true);
	}

	async findOne(params = {}, force = false) {
		params.where = params.where ?? {};

		if (this.#configs.softDelete) {
			params.where.deletedAt = {
				op: "==",
				value: null,
			};
		}

		const { newParams, newModel } = paramsConstructor(this.#keys, params, { relations: this.#relations, children: this.#children });

		let firstData = {};

		const cache = getCache();

		if (cache[this.path + JSON.stringify(params.where)] && !force) {
			firstData = {...cache[this.path + JSON.stringify(params.where)]};
		} else {
			let found = false;
			if(cache[this.path]){
				const [data] = cache[this.path].filter((row) =>
					newParams.where_after.reduce((acc, func) => {
						if (acc) {
							acc = func(row);
						}
						return acc;
					}, true)
				)
				if(!!data){
					found = true;
					firstData = {...data};
				}
			}

			if(!found){
				const [data] = ((await FirebaseService.getCollection(this.path, newParams.where)) ?? []).filter((row) =>
					newParams.where_after.reduce((acc, func) => {
						if (acc) {
							acc = func(row);
						}
						return acc;
					}, true)
				);

				firstData = data
			}

			store.dispatch(
				setCache({
					[this.path + JSON.stringify(params.where)]: {...firstData},
				})
			);
		}

		if (firstData) {
			if (params.join?.length > 0) {
				await Promise.all(
					Object.keys(newParams.join).map((key) => {
						const getJoinData = async () => {
							firstData[newParams.join[key].name] = await newParams.join[key].model.findById(firstData[newParams.join[key].fk]);
						};

						return getJoinData();
					})
				);
			}

			if (params.children?.length > 0) {
				await Promise.all(
					Object.keys(newParams.children).map((key) => {
						const getJoinData = async () => {
							firstData[newParams.children[key].name] = await this.id(firstData.id)[key].findAll();
						};

						return getJoinData();
					})
				);
			}
		}

		return dataFormatter(newModel, this.#defaults, firstData, true);
	}

	async findById(id, params = {}, force = false) {
		let firstData = {};

		const cache = getCache();

		if (cache[this.path + id] && !force) {
			firstData = {...cache[this.path + id]};
		} else {
			let found = false;
			if(cache[this.path]){
				const data = cache[this.path].find((r) => r.id === id);
				if(!!data){
					found = true;
					firstData = {...data};
				}
			}

			if(!found){
				firstData = await FirebaseService.getDoc(this.path, id);
			}

			store.dispatch(
				setCache({
					[this.path + id]: {...firstData},
				})
			);
		}

		const { newParams, newModel } = paramsConstructor(this.#keys, params, { relations: this.#relations, children: this.#children });

		if (params.join?.length > 0) {
			await Promise.all(
				Object.keys(newParams.join).map((key) => {
					const getJoinData = async () => {
						firstData[newParams.join[key].name] = await newParams.join[key].model.findById(firstData[newParams.join[key].fk]);
					};

					return getJoinData();
				})
			);
		}

		if (params.children?.length > 0) {
			await Promise.all(
				Object.keys(newParams.children).map((key) => {
					const getJoinData = async () => {
						firstData[newParams.children[key].name] = await this.id(firstData.id)[key].findAll();
					};
					return getJoinData();
				})
			);
		}

		if (firstData.deletedAt === null || !this.#configs.softDelete) {
			return dataFormatter(newModel, this.#defaults, firstData, true);
		}
	}

	async create(data, id) {
		const newModel = { ...this.#keys };

		if (this.#configs.dateLogs) {
			data.createdAt = new Date();
		}

		if (this.#configs.tracked) {
			data.id_user = userID();
		}

		delete newModel.id;

		const newRow = await FirebaseService.createData(this.path, dataFormatter(newModel, this.#defaults, data), id);

		if (getCache()[this.path]) {
			store.dispatch(
				setCache({
					[this.path]: [...(getCache()[this.path] ?? []), newRow],
					[this.path + newRow.id]: newRow,
				})
			);
		}

		return newRow;
	}

	async update(id, data) {
		if (this.#configs.dateLogs) {
			data.updatedAt = new Date();
		}

		if (this.#configs.tracked) {
			data.id_user = userID();
		}

		if (getCache()[this.path]) {
			let newRow = {}
			store.dispatch(
				setCache({
					[this.path]: [
						...getCache()[this.path]?.map((row) => {
							if (row.id === id) {
								newRow = { ...row, ...data };
								row = newRow;
							}

							return row;
						}),
					],
					[this.path + id]: newRow,
				})
			);
		}

		return FirebaseService.updateData(this.path, id, dataFormatter(this.#keys, {}, data, true));
	}

	async delete(id, force = false) {
		if (getCache()[this.path]) {
			store.dispatch(
				setCache({
					[this.path]: getCache()[this.path].filter((row) => row.id !== id),
					[this.path + id]: undefined,
				})
			);
		}

		if (this.#configs.softDelete && !force) {
			const data = { deletedAt: new Date() };

			if (this.#configs.tracked) {
				data.id_user = userID();
			}

			return await FirebaseService.updateData(this.path, id, dataFormatter(this.#keys, {}, data, true));
		} else {
			return await FirebaseService.deleteData(this.path, id);
		}
	}
}

const initFunction = {
	new: (keys, configs) => {
		keys.id = FirebaseDatatypeEnum.STRING;
		if(configs.dateLogs){
			keys.createdAt = FirebaseDatatypeEnum.TIMESTAMP;
			keys.updatedAt = FirebaseDatatypeEnum.TIMESTAMP;
		}
		if (configs.softDelete) keys.deletedAt = FirebaseDatatypeEnum.TIMESTAMP;
		if (configs.tracked) keys.id_user = FirebaseDatatypeEnum.STRING;

		return new Model({ keys, configs });
	},
};

const models = [User, Categoria, Cliente, ClienteAnotacao, FormaPagamento, Fornecedor, Pedido, Produto, ProdutoFluxo, Despesa, Receita, Configuracao].reduce(
	(acc, model) => {
		const newModel = model(initFunction, FirebaseDatatypeEnum);

		acc[newModel.ModelName] = newModel;

		return acc;
	},
	{}
);

Object.keys(models).forEach((key) => {
	models[key].doConnections(models);
});

export default models;
